import { User } from '@src/entities/User/ui/components/User';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { fieldValidator } from '@src/shared/includes/validation';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { isEmpty, merge } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import fields from './includes/fields.json';

const { categoryCode, categoryDesc, code, description, enabled } = fields;

/**
 * FormModal
 *
 * Prompts the user with a popup window allowing them to edit the tag.
 *
 * @param currentEntity
 * @param handleSave
 * @param isOpen
 * @param setCurrentEntity
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function FormModal({
  currentEntity = {},
  handleSave = async () => {},
  isOpen = false,
  setCurrentEntity = () => {},
  setIsOpen = () => {},
}) {
  let mode = 'create';

  if (!isEmpty(currentEntity)) {
    mode = 'edit';
  }

  const defaultValues = {
    categoryCode: '',
    categoryDesc: '',
    code: '',
    description: '',
    enabled: true,
  };

  const [initialValues, setInitialValues] = useState({});

  const form = useForm({ defaultValues });

  const { hasPermission } = useCurrentUser();

  const canCreate = hasPermission('Admin:Tags', 'create');
  const canEdit = hasPermission('Admin:Tags', 'edit');

  const canWrite = useMemo(() => {
    if (mode === 'create') {
      return canCreate;
    }

    if (mode === 'edit') {
      return canEdit;
    }
  }, [canEdit, canCreate, mode]);

  const statusValue = form?.getValues('isValid');

  const canActivate = hasPermission('Admin:Tags', 'activate');
  const canDeactivate = hasPermission('Admin:Tags', 'deactivate');

  const canToggle = useMemo(() => {
    if (statusValue === true) {
      return canDeactivate;
    }

    if (statusValue === false) {
      return canActivate;
    }
  }, [statusValue, canDeactivate, canActivate]);

  /**
   * Mapping data loaded from API to the form state.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      const data = merge({}, defaultValues, {
        categoryCode: currentEntity?.categoryCode,
        categoryDesc: currentEntity?.categoryDesc,
        code: currentEntity?.code,
        description: currentEntity?.description,
        enabled: currentEntity?.enabled,
      });

      if (isEmpty(initialValues)) {
        setInitialValues(data);
      }

      form.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });
    }
  }, [currentEntity]);

  /**
   * handleClose
   *
   * Handles the closing of the modal.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    setIsOpen(false);
    setCurrentEntity({});
  };

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @param submittedValues
   * @returns {Promise<void>}
   */
  const handleSubmit = async (submittedValues) => {
    const payload = {};

    payload.categoryCode = String(submittedValues?.categoryCode);
    payload.categoryDesc = String(submittedValues?.categoryDesc);
    payload.code = String(submittedValues?.code);
    payload.description = String(submittedValues?.description);
    payload.enabled = Boolean(submittedValues?.enabled);

    if (mode === 'create') {
      payload.categoryCode = `X${payload.categoryCode}`;
    }

    await handleSave({ data: payload, mode });

    await handleClose();
  };

  const formValues = form.getValues();

  /**
   * isEqual
   *
   * Compares two objects to determine if they are equal.
   *
   * @param object1
   * @param object2
   * @returns {boolean}
   */
  const isEqual = (object1, object2) => {
    return JSON.stringify(object1) === JSON.stringify(object2);
  };

  /**
   * form validation rules, toggling the save button enablement.
   */
  const isDisabled = useMemo(() => {
    return !form.formState.isDirty || isEqual(formValues, initialValues) || !isEmpty(form.formState.errors);
  }, [form.formState.isDirty, formValues, initialValues, form.formState.errors]);

  return (
    <ErrorHandler location="src/entities/Tag/ui/components/FormModal/FormModal.jsx">
      <Modal isOpen={isOpen} onClose={handleClose} title={mode === 'edit' ? 'Edit Tag' : 'Create Tag'}>
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Grid>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <User.Capability
                  alias="write"
                  attributes={['create', 'edit']}
                  isAllowed={canWrite}
                  resource="Admin:Tags"
                >
                  <TextInput
                    {...categoryCode}
                    isDisabled={mode === 'edit'}
                    onChange={(event) => {
                      form?.setValue(`categoryCode`, String(event?.target?.value).toUpperCase());
                      form?.validate(
                        `categoryCode`,
                        () => {},
                        () => {}
                      );
                    }}
                  />
                </User.Capability>
              </Grid.Col>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                {' '}
                <User.Capability
                  alias="write"
                  attributes={['create', 'edit']}
                  isAllowed={canWrite}
                  resource="Admin:Tags"
                >
                  <TextInputArea
                    {...categoryDesc}
                    isDisabled={mode === 'edit' && !currentEntity?.userDefined}
                    onChange={() => {
                      form?.validate(
                        `categoryDesc`,
                        () => {},
                        () => {}
                      );
                    }}
                  />
                </User.Capability>
              </Grid.Col>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <User.Capability
                  alias="write"
                  attributes={['create', 'edit']}
                  isAllowed={canWrite}
                  resource="Admin:Tags"
                >
                  <TextInput
                    {...code}
                    isDisabled={mode === 'edit'}
                    onChange={(event) => {
                      form?.setValue(`code`, String(event?.target?.value).toUpperCase());
                      form?.validate(
                        `code`,
                        () => {},
                        () => {}
                      );
                    }}
                    validators={{
                      ...code?.validators,
                      ...{
                        validate: {
                          customValidator: (value) => {
                            return fieldValidator(code, value);
                          },
                        },
                      },
                    }}
                  />
                </User.Capability>
              </Grid.Col>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <User.Capability
                  alias="write"
                  attributes={['create', 'edit']}
                  isAllowed={canWrite}
                  resource="Admin:Tags"
                >
                  <TextInputArea
                    {...description}
                    onChange={() => {
                      form?.validate(
                        `description`,
                        () => {},
                        () => {}
                      );
                    }}
                  />
                </User.Capability>
              </Grid.Col>
              {mode === 'edit' && (
                <Grid.Col span={{ xs: '100%' }}>
                  <Layout.Group>
                    <User.Capability
                      alias="toggle"
                      attributes={['activate', 'deactivate']}
                      isAllowed={canToggle}
                      resource="Admin:Tags"
                    >
                      <ToggleSwitch {...enabled} isDisabled={!currentEntity?.userDefined} />
                    </User.Capability>
                  </Layout.Group>
                </Grid.Col>
              )}
            </Grid>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button data-testid="action-button" isDisabled={isDisabled} type="submit" variant="solid">
                {mode === 'edit' ? 'Save' : 'Create'}
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
